import { Flex, Stack, Text } from '@chakra-ui/react';

import AboutSection from './sections/AboutSection';
import CircleEditButton from '@/client/components/shared/buttons/CircleEditButton';
import CompanyInformationSection from './sections/CompanyInformationSection';
import LearnerBadgesList from '@/client/components/learners/badges/LearnerBadgesList';
import SkillsSection from './sections/SkillsSection';
import SocialProfilesSection from './sections/SocialProfilesSection';
import type { User as UserType } from '@/client/types/User';
import { useAuthStore } from '@/client/services/state/authStore';
import { useLearnerProfileStore } from '../state/learnerProfileStore';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function UserDetailsSection({
  label,
  helpText,
  sectionId,
  children,
}: {
  label: string;
  helpText: string | undefined;
  sectionId?: string;
  children: React.ReactNode;
}) {
  const { isActiveUser, setUserDetailsModalIsOpen, setScrollToSection } = useLearnerProfileStore();

  const handleEditClick = () => {
    if (sectionId) {
      setScrollToSection(sectionId);
    }
    setUserDetailsModalIsOpen(true);
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={0}>
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontSize="18px" fontWeight={600} color="baseBlack">
            {label}
          </Text>

          {isActiveUser && <CircleEditButton colorScheme="neutral" onClick={handleEditClick} />}
        </Flex>

        {helpText && (
          <Text fontSize="14px" fontWeight={400} color="neutral.800">
            {helpText}
          </Text>
        )}
      </Stack>

      {children}
    </Stack>
  );
}

interface LearnerProfileUserDetailsProps {
  userData: UserType;
}

export default function LearnerProfileUserDetails({ userData }: LearnerProfileUserDetailsProps) {
  const { t } = useTranslation();
  const { id: paramsId } = useParams();
  const { isActiveUser } = useLearnerProfileStore();
  const { authConfig } = useAuthStore();
  const { company, user } = authConfig;

  const { first_name: firstName, metadata } = userData;

  const userId = isActiveUser ? user._id : paramsId;

  const sections = [
    {
      render: true,
      label: t('learners.profile.userDetails.aboutTitle', { name: firstName }),
      sectionId: 'about',
      children: <AboutSection userData={userData} />,
    },
    {
      render: isActiveUser && metadata && Object.keys(metadata).length > 0,
      label: t('learners.profile.userDetails.companyInformation.title'),
      sectionId: 'company-information',
      helpText: t('learners.profile.userDetails.companyInformation.helpText'),
      children: <CompanyInformationSection userData={userData} />,
    },
    {
      render: company.feature_flags.enable_social_profiles,
      label: t('learners.profile.userDetails.socialProfiles.title'),
      sectionId: 'social-profiles',
      children: <SocialProfilesSection userData={userData} />,
    },
    {
      render: true,
      label: t('learners.profile.userDetails.skills.title'),
      sectionId: 'skills',
      children: <SkillsSection userData={userData} />,
    },
  ];

  return (
    <Stack spacing={10}>
      {sections.map(
        (section) =>
          section.render && (
            <UserDetailsSection
              key={section.label}
              label={section.label}
              helpText={section.helpText}
              sectionId={section.sectionId}
            >
              {section.children}
            </UserDetailsSection>
          ),
      )}

      <Stack spacing={4}>
        <Text fontSize="18px" fontWeight={600} color="baseBlack">
          {t('learners.badges')}
        </Text>

        <LearnerBadgesList
          userId={userId}
          modalNoDataCopy={t('learners.badges.noneFound')}
          noDataPlaceholder={
            <Text fontSize="16px" fontWeight={400} textDecoration="italic" color="neutral.800">
              {t('learners.profile.noBadges')}
            </Text>
          }
        />
      </Stack>
    </Stack>
  );
}
